import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ProjectCard from "./ProjectCard";
import ButtonElement from "@/components/elements/Button/Button";
import { useNavigate } from "react-router-dom";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import { fetchProjectCards } from "@/lib/queries";
import { fetchLocalProjects, type LocalImageVariants } from "@/lib/localProjects";
import { preloadImages } from "@/lib/preloadImages";
import type { SanityImageData } from "@/types/sanity";

interface CompletedProject {
  _id?: string;
  id?: string;
  title: string;
  category: string;
  subCategory?: string | string[] | null;
  slug?: string | null;
  /** Static image URL (local projects). */
  imageUrl?: string;
  /** Thumbnails for the local image, when they exist. */
  variants?: LocalImageVariants;
  /** Sanity image (CMS projects). */
  image?: SanityImageData | null;
}

const COMPLETED_CATEGORY = "Completed Projects";

/** Cards visible without scrolling load eagerly; the rest wait until they are near the screen. */
const EAGER_CARDS = 6;
/** How many local thumbnails to start downloading while the CMS request is still in flight. */
const PRELOAD_COUNT = 9;

const COMPLETED_SUBCATEGORIES = [
  "Hotel, Hydropower, Parks, Institutional Buildings",
  "Residential Building",
];

const subcategoryMap: { [key: string]: string[] } = {
  "Hotel, Hydropower, Parks, Institutional Buildings": [
    "Hotel",
    "Hydropower",
    "Parks",
    "Institutional Buildings",
  ],
  "Residential Building": ["Residential"],
};

const cardItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

const CompletedSubcategoryPage = () => {
  const navigate = useNavigate();
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>(
    COMPLETED_SUBCATEGORIES[0]
  );
  const [projectList, setProjectList] = useState<CompletedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [animateCards, setAnimateCards] = useState(true);
  const previousCategoryRef = useRef<string | null>(null);

  // CMS and local projects are independent sources: load them in parallel and show
  // whatever succeeds. Only report an error when both fail.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadProjects = async () => {
      // The local list arrives within milliseconds, long before the CMS answers. Start
      // fetching its thumbnails right away so they are cached by the time the cards render.
      const localRequest = fetchLocalProjects(signal).then((projects) => {
        preloadImages(
          projects
            .filter((project) => project.category === COMPLETED_CATEGORY)
            .slice(0, PRELOAD_COUNT)
            .map((project) => project.variants?.src ?? project.imageUrl)
        );
        return projects;
      });

      const [sanityResult, localResult] = await Promise.allSettled([
        fetchProjectCards(COMPLETED_CATEGORY, { signal }),
        localRequest,
      ]);

      if (signal.aborted) return;

      if (sanityResult.status === "rejected") {
        console.error("Failed to load projects from Sanity:", sanityResult.reason);
      }
      if (localResult.status === "rejected") {
        console.error("Failed to load local projects:", localResult.reason);
      }

      if (sanityResult.status === "rejected" && localResult.status === "rejected") {
        const reason = sanityResult.reason;
        setError(reason instanceof Error ? reason.message : "Error fetching projects");
        setLoading(false);
        return;
      }

      // Sanity projects: most recently updated first. Local projects keep file order.
      const sanityProjects: CompletedProject[] =
        sanityResult.status === "fulfilled"
          ? [...sanityResult.value].sort(
              (a, b) =>
                new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime()
            )
          : [];

      const localProjects: CompletedProject[] =
        localResult.status === "fulfilled"
          ? localResult.value.filter((project) => project.category === COMPLETED_CATEGORY)
          : [];

      setProjectList([...sanityProjects, ...localProjects]);
      setLoading(false);
    };

    loadProjects();

    return () => controller.abort();
  }, []);

  // Normalize subCategory field to always be an array for filtering
  const normalizeSubCategory = (subCategory: CompletedProject["subCategory"]): string[] => {
    if (!subCategory) return [];
    if (Array.isArray(subCategory)) return subCategory;
    if (typeof subCategory === "string") return [subCategory];
    return [];
  };

  // Filter projects by selected subcategory
  const filteredProjects = projectList.filter((project) => {
    // Normalize subCategory for each project
    const normalizedSub = normalizeSubCategory(project.subCategory);

    // Check category and if any of the project's subcategories match the selected group
    return (
      project.category === COMPLETED_CATEGORY &&
      subcategoryMap[selectedSubcategory].some((sub) =>
        normalizedSub.includes(sub)
      )
    );
  });

  useEffect(() => {
    const prev = previousCategoryRef.current;
    setAnimateCards(prev !== selectedSubcategory);
    previousCategoryRef.current = selectedSubcategory;
  }, [selectedSubcategory]);

  if (loading)
    return (
      <div className="mt-[5rem] min-h-screen pt-24 text-center text-lg text-gray-500">
        Loading projects...
      </div>
    );

  if (error)
    return (
      <div className="mt-[5rem] min-h-screen pt-24 text-center text-red-600">
        Error loading projects: {error}
      </div>
    );

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-20 md:px-15 px-5 my-[5rem] ">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Completed Projects" level={1} />
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-10 px-5 md:px-0">
        {/* Sidebar for selecting subcategory */}
        <div className="relative w-full max-w-[320px] mx-auto 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem] flex flex-col">
          {COMPLETED_SUBCATEGORIES.map((subcat) => {
            const isActive = selectedSubcategory === subcat;
            return (
              <ButtonElement
                key={subcat}
                btnText={subcat}
                onClick={() => setSelectedSubcategory(subcat)}
                className={`w-full rounded-sm !py-3 mb-2 text-left ${
                  isActive
                    ? "bg-custom-secolor font-semibold border-l-4 border-white bg-opacity-70"
                    : "bg-custom-secolor hover:bg-custom-secolor"
                }`}
              />
            );
          })}
        </div>

        {/* Projects Grid */}
        <div className="flex-1">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <motion.div
                  key={`${project._id || project.id}-${selectedSubcategory}-${animateCards}`}
                  variants={cardItemVariants}
                  initial={animateCards ? "hidden" : false}
                  animate={animateCards ? "visible" : false}
                >
                  <ProjectCard
                    id={project.id || project._id}
                    slug={project.slug ?? undefined}
                    imageUrl={project.imageUrl}
                    variants={project.variants}
                    image={project.image}
                    priority={index < EAGER_CARDS}
                    title={project.title}
                    category={project.category}
                  />
                </motion.div>
              ))
            ) : (
              <div className="text-center col-span-full text-gray-500">
                No projects found in this category.
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <button
        className="mt-10 text-gray-500 hover:text-custom-hov hover:underline transition duration-200 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem]"
        onClick={() => navigate("/projects")}
      >
        ← Back to Project Categories
      </button>
    </div>
  );
};

export default CompletedSubcategoryPage;
